import { API_BASE_URL } from '../config/api';
import { apiClient } from '../services/api';
import type { ConnectivityState } from './types';

type Listener = (state: ConnectivitySnapshot) => void;
export interface ConnectivitySnapshot {
  state: ConnectivityState;
  browser_online: boolean;
  api_reachable: boolean;
  authenticated: boolean | null;
  latency_ms: number | null;
  last_success_at: string | null;
  checked_at: string;
}

let snapshot: ConnectivitySnapshot = {
  state: navigator.onLine ? 'NETWORK_PRESENT_API_UNREACHABLE' : 'OFFLINE',
  browser_online: navigator.onLine,
  api_reachable: false,
  authenticated: null,
  latency_ms: null,
  last_success_at: null,
  checked_at: new Date().toISOString(),
};
const listeners = new Set<Listener>();
let timer: number | null = null;
let inflight: Promise<ConnectivitySnapshot> | null = null;

function publish(next: ConnectivitySnapshot): ConnectivitySnapshot {
  snapshot = next;
  listeners.forEach(listener => {
    try { listener(next); } catch { /* listener isolation */ }
  });
  return next;
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timerId = window.setTimeout(() => controller.abort(), timeoutMs);
  try { return await fetch(url, { ...init, signal: controller.signal, cache: 'no-store' }); }
  finally { window.clearTimeout(timerId); }
}

export async function probeConnectivity(): Promise<ConnectivitySnapshot> {
  if (inflight) return inflight;
  inflight = (async () => {
    const browserOnline = navigator.onLine;
    const checkedAt = new Date().toISOString();
    if (!browserOnline) {
      return publish({ ...snapshot, state: 'OFFLINE', browser_online: false, api_reachable: false, authenticated: null, latency_ms: null, checked_at: checkedAt });
    }

    const started = performance.now();
    try {
      const health = await fetchWithTimeout(
        `${API_BASE_URL}/runtime-info`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        },
        5000
      );

      const latency = Math.round(performance.now() - started);
      const contentType = health.headers.get('content-type') || '';

      if (!health.ok || !contentType.includes('application/json')) {
        return publish({
          ...snapshot,
          state: health.status >= 500
            ? 'BACKEND_UNAVAILABLE'
            : 'NETWORK_PRESENT_API_UNREACHABLE',
          browser_online: true,
          api_reachable: false,
          authenticated: null,
          latency_ms: latency,
          checked_at: checkedAt,
        });
      }

        let authenticated: boolean | null = null;

        try {
          // Canonical authentication authority.
          // The central API client renews an expired short-lived access
          // cookie through /auth/refresh before declaring the session expired.
          await apiClient.get('/auth/me');
          authenticated = true;
        } catch (error: any) {
          if (error?.response?.status === 401) {
            return publish({
              state: 'AUTH_REQUIRED',
              browser_online: true,
              api_reachable: true,
              authenticated: false,
              latency_ms: latency,
              last_success_at: snapshot.last_success_at,
              checked_at: checkedAt,
            });
          }

          // runtime-info already proved that the backend is reachable.
          // A timeout or transient authenticated probe failure is not
          // authoritative proof that the user session has ended.
        }
      const state: ConnectivityState = latency > 2000 ? 'ONLINE_DEGRADED' : 'ONLINE';
      return publish({ state, browser_online: true, api_reachable: true, authenticated, latency_ms: latency, last_success_at: new Date().toISOString(), checked_at: checkedAt });
    } catch {
      return publish({ ...snapshot, state: 'NETWORK_PRESENT_API_UNREACHABLE', browser_online: true, api_reachable: false, authenticated: null, latency_ms: null, checked_at: checkedAt });
    }
  })().finally(() => { inflight = null; });
  return inflight;
}

export function getConnectivitySnapshot(): ConnectivitySnapshot { return snapshot; }
export function backendUsable(state = snapshot.state): boolean { return state === 'ONLINE' || state === 'ONLINE_DEGRADED'; }
export function subscribeConnectivity(listener: Listener): () => void { listeners.add(listener); return () => listeners.delete(listener); }

export function startConnectivityEngine(intervalMs = 30000): () => void {
  const wake = () => { void probeConnectivity(); };
  window.addEventListener('online', wake);
  window.addEventListener('offline', wake);
  document.addEventListener('visibilitychange', wake);
  void probeConnectivity();
  if (timer !== null) window.clearInterval(timer);
  timer = window.setInterval(wake, intervalMs);
  return () => {
    window.removeEventListener('online', wake);
    window.removeEventListener('offline', wake);
    document.removeEventListener('visibilitychange', wake);
    if (timer !== null) window.clearInterval(timer);
    timer = null;
  };
}
